// theme.ts
import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
	interface Theme {
		palette: Palette;
	}
	interface Palette {
		border: {
			main: string;
		};
	}
	interface PaletteOptions {
		border?: {
			main: string;
		};
	}
	interface ThemeOptions {
		palette?: PaletteOptions;
	}
}

const theme = createTheme({
  direction: 'ltr',
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2e7d32',
      contrastText: '#ffffff',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#e43024',
    },
    info: {
      main: '#0288d1',
    },
    success: {
      main: '#2e7d32',
    },
    background: {
      default: '#CCCCCC',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
    border: {
      main: '#CCCCCC',
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: { fontSize: '2.5rem', fontWeight: 600 },
    h2: { fontSize: '2rem', fontWeight: 600 },
    h3: { fontSize: '1.75rem', fontWeight: 500 },
    h4: { fontSize: '1.5rem', fontWeight: 500 },
    h5: { fontSize: '1.25rem', fontWeight: 500 },
    h6: { fontSize: '1rem', fontWeight: 500 },
    button: { textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          // ograniczenie szerokości inputa
          // zawsze pełna szerokość w kontenerze
          borderRadius: 8,
          backgroundColor: theme.palette.background.paper,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
          },
        }),
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.border.main,
          borderWidth: 1.5, // domyślnie grubsza ramka
        }),
        input: {
          padding: '14px',
          fontSize: '1rem', // trochę większy font
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '1rem', // większy font labela
          color: theme.palette.text.primary,
          fontWeight: 500,
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginLeft: 0,
          fontSize: '0.8rem',
          color: theme.palette.text.secondary,
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0px 8px', // odstępy między wierszami
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          '& .MuiTableCell-root': {
            fontWeight: 600,
            fontSize: '0.85rem',
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            borderBottom: `2px solid ${theme.palette.border.main}`,
            [theme.breakpoints.down('md')]: {
              fontSize: '0.75rem',
            },
            [theme.breakpoints.down('sm')]: {
              fontSize: '0.7rem',
            },
          },
        }),
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiTableRow-root': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 8,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.border.main}`,
          fontSize: '0.9rem',
          padding: '12px 16px',
          color: theme.palette.text.primary,
        }),
        head: ({ theme }) => ({
          fontWeight: 600,
          color: theme.palette.text.secondary,
        }),
        body: (theme) => ({
          fontWeight: 400,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td, &:last-child th': {
            borderBottom: 0,
          },
        },
      },
    },
  },
});

export default theme;
